import { Aviso, Botao, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { useReprocessarIssueDoAtd, type FormularioDoChamado } from "../../nucleo/tickets";

const ROTULO_DO_ESTADO_DA_ISSUE = {
  pendente: "Pendente",
  criado: "Criada",
  falhou: "Falhou",
} as const;

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <dt className="nota-rodape">{rotulo}</dt>
      <dd className="text-right font-medium">{valor}</dd>
    </div>
  );
}

/** A issue do ATD: só a equipe vê, com o motivo escrito quando ela ainda não existe. */
function IssueNoAtd({
  ticketId,
  espelho,
  podeReprocessar,
}: {
  ticketId: string;
  espelho: NonNullable<FormularioDoChamado["espelho"]>;
  podeReprocessar: boolean;
}) {
  const reprocessar = useReprocessarIssueDoAtd(ticketId);
  const precisaDeAtencao =
    espelho.estado !== "criado" || espelho.anexos_pendentes > 0 || espelho.edicao_pendente;
  return (
    <div className="mt-3" data-testid="issue-no-atd" data-estado-da-issue={espelho.estado}>
      <div className="flex items-center gap-2">
        <strong>Issue no ATD</strong>
        <Chip estado={espelho.estado === "criado" ? "ok" : espelho.estado === "falhou" ? "erro" : "atencao"}>
          {ROTULO_DO_ESTADO_DA_ISSUE[espelho.estado]}
        </Chip>
        {espelho.chave ? <span data-testid="chave-da-issue">{espelho.chave}</span> : null}
      </div>
      {espelho.estado !== "criado" ? (
        <p className="nota-rodape mt-1">A issue ainda não foi criada no Jira; o chamado segue normal no portal.</p>
      ) : null}
      {espelho.ultimo_erro ? (
        <Aviso tom={espelho.estado === "falhou" ? "erro" : "atencao"} className="mt-2" data-testid="pendencia-da-issue">
          {espelho.ultimo_erro}
        </Aviso>
      ) : null}
      {espelho.edicao_pendente ? (
        <Aviso tom="atencao" className="mt-2" data-testid="edicao-da-issue">
          Os campos Projeto e Origem ainda não foram aplicados à issue.
          {espelho.edicao_erro ? ` ${espelho.edicao_erro}` : ""}
        </Aviso>
      ) : null}
      {espelho.anexos_pendentes > 0 ? (
        <Aviso tom="atencao" className="mt-2" data-testid="anexos-da-issue">
          {espelho.anexos_pendentes} anexo(s) ainda não foram para a issue.
          {espelho.anexos_erro ? ` ${espelho.anexos_erro}` : ""}
        </Aviso>
      ) : null}
      {reprocessar.isError ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(reprocessar.error)}</Aviso> : null}
      {podeReprocessar && precisaDeAtencao ? (
        <Botao
          type="button"
          tamanho="pequeno"
          variante="fantasma"
          className="mt-2"
          disabled={reprocessar.isPending}
          onClick={() => reprocessar.mutate()}
        >
          {reprocessar.isPending ? "Reprocessando…" : "Reprocessar issue no ATD"}
        </Botao>
      ) : null}
    </div>
  );
}

/** O que o cliente preencheu no formulário e, para a equipe, o estado da issue espelho no ATD. */
export function FormularioNoDetalhe({
  ticketId,
  formulario,
  podeReprocessar,
}: {
  ticketId: string;
  formulario: FormularioDoChamado | null | undefined;
  podeReprocessar: boolean;
}) {
  if (!formulario) return null;
  return (
    <Cartao titulo="Classificação do chamado" className="mb-4">
      <dl data-testid="formulario-do-chamado">
        {formulario.modulo ? <Linha rotulo="Módulo" valor={formulario.modulo} /> : null}
        <Linha rotulo="Tipo" valor={formulario.tipo_rotulo} />
        <Linha rotulo="Impacto" valor={formulario.impacto_rotulo} />
        {formulario.ambiente ? <Linha rotulo="Ambiente" valor={formulario.ambiente} /> : null}
        {formulario.versao ? <Linha rotulo="Versão" valor={formulario.versao} /> : null}
        {formulario.responsavel_ti ? <Linha rotulo="Responsável de TI" valor={formulario.responsavel_ti} /> : null}
      </dl>
      {formulario.espelho ? (
        <IssueNoAtd ticketId={ticketId} espelho={formulario.espelho} podeReprocessar={podeReprocessar} />
      ) : null}
    </Cartao>
  );
}
